import { Button } from '@app/components/various/Button';
import MeasurementChart from '@app/components/various/Chart';
import { Measurement } from '@app/components/various/Icons';
import { Popover } from '@app/components/various/Popover';
import { convertToTableFormat, getIsValidMeasurementChart } from '@app/ducks/helpers';
import { getSafeInnerHTML } from '@app/utils/getSafeInnerHtml';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  productDetails: Product.Standard;
  activeVariant: Product.Standard;
}

export const ProductDetailsDescription = ({ productDetails, activeVariant }: Props) => {
  const { t } = useTranslation(['products']);

  const { excerpt, description } = activeVariant;

  const measurementChartTable = React.useMemo(
    () => (getIsValidMeasurementChart(productDetails.measurementChart) ? convertToTableFormat(productDetails.measurementChart) : null),
    [productDetails],
  );

  const shouldShowDescription = !isEmpty(excerpt) || !isEmpty(description);
  const shouldShowMeasurementChartTable = isDefined(measurementChartTable);

  const ref = React.useRef<HTMLDivElement>(null);

  if (!shouldShowDescription && !shouldShowMeasurementChartTable) {
    return null;
  }

  return (
    <div ref={ref} className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        {shouldShowDescription && <h3 className="heading-3">{t('products:product_description')}</h3>}
        {shouldShowMeasurementChartTable && (
          <Popover
            content={<MeasurementChart table={measurementChartTable} />}
            contentHeader={t('products:measurement_chart')}
            possiblePlacements={[
              'top-start',
              'bottom-start',
              'left-start',
              'left-end',
              'left-center',
              'right-center',
              'right-start',
              'right-end',
            ]}
            placement="bottom-start"
            isClickable
          >
            <Button variant="text" color="secondary" icon={Measurement}>
              {t('products:measurement_chart')}
            </Button>
          </Popover>
        )}
      </div>
      {shouldShowDescription && (
        <div className="product-description">
          {/* eslint-disable-next-line react/no-danger */}
          {!isEmpty(excerpt) && <p dangerouslySetInnerHTML={getSafeInnerHTML(excerpt)} />}
          {/* eslint-disable-next-line react/no-danger */}
          {!isEmpty(description) && <div dangerouslySetInnerHTML={getSafeInnerHTML(description)} />}
        </div>
      )}
    </div>
  );
};
